/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { RefreshRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { CircularProgress, Stack } from '@mui/material';
import { Box } from '@mui/system';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  TotalUsageByLink,
  TotalUsageByNetwork,
  ServiceAvailability,
  ServiceAvailabilityValues,
} from '../../shared/entities/dataUsage';
import {
  selectCumulativeUsageByLink,
  selectCumulativeUsageByNetwork,
  selectDataUsageLoading,
  selectTotalUsageByLink,
  selectTotalUsageByNetwork,
  selectUsageByLink,
  selectUsageByNetwork,
  selectTotalPacketLossByLink,
  selectServiceAvailability,
  TerminalListItem,
} from '../../store';
import {
  clearDataUsage,
  requestCumulativeUsageByNetwork,
  requestTotalPacketLossByLink,
  requestTotalUsageByLink,
  requestTotalUsageByNetwork,
  requestUsageByLink,
  requestUsageByLinkCumulative,
  requestUsageByNetwork,
  requestServiceAvailability,
} from '../../store/dataUsage/actions';
import { DATA_USAGE_PAGE_MAP_ID, FLEET_MAP_ID, setMapHidden } from '../../store/maps/actions';
import { getTerminalsOnce } from '../../store/terminals/actions';
import appTheme from '../common/theme/appTheme.json';
import DownloadAllChartsDialog from './chartDownload/DownloadAllChartsDialog';
import CumulativeLinkUsageChart from './charts/CumulativeLinkUsageChart';
import CumulativeNetworkUsageChart from './charts/CumulativeNetworkUsageChart';
import LinkUsageChart from './charts/LinkUsageChart';
import NetworkUsageChart from './charts/NetworkUsageChart';
import TotalLinkUsageChart from './charts/TotalLinkUsageChart';
import TotalNetworkUsageChart from './charts/TotalNetworkUsageChart';
import PerformanceSummaryChart from './charts/PerformanceSummaryChart';
import DataUsageDatePicker from './DataUsageDatePicker';
import DataUsageErrorFeedback from './DataUsageErrorFeedback';
import DataUsageHeader from './DataUsageHeader';
import DataUsageTerminalSelector from './DataUsageTerminalSelector';
import { SEC_TO_MS_CONVERSION } from '../../utils/DateTimeUtils';
import { isNil } from 'lodash';
import ServiceAvailabilityChart from './charts/ServiceAvailabilityChart';
import DataUsageChartsSelector from './DataUsageChartsSelector';

export const DATA_USAGE_CHART_HEIGHT = 440;
export const DATA_USAGE_CHART_HEADING_HEIGHT = 57;
export const DATA_USAGE_CONTAINER_MIN_WIDTH_PX = 1550;
export const DATA_USAGE_CONTAINER_OFFSET_PX = 52;

export const calculateTotalServiceAvailabilityPercentage = (serviceAvailability: ServiceAvailability) => {
  const serviceAvailabilities = Object.values(serviceAvailability?.serviceAvailability || {});
  // If there are no service availability entries, return 0
  let totalServiceAvailability = 0;
  if (serviceAvailabilities.length) {
    const serviceAvailabilityTotaled = serviceAvailabilities.reduce(
      (accumulatedServiceAvailability: number, currentServiceAvailabilityValue: ServiceAvailabilityValues) =>
        accumulatedServiceAvailability + currentServiceAvailabilityValue.serviceAvailabilityPercentage,
      0,
    );
    totalServiceAvailability = Math.round((serviceAvailabilityTotaled / serviceAvailabilities.length) * 10) / 10;
  }
  return totalServiceAvailability;
};

const DataUsageContainer = () => {
  // 3rd party hooks
  const dispatch = useDispatch();

  // Selectors
  const totalUsageByNetwork: TotalUsageByNetwork | undefined = useSelector(selectTotalUsageByNetwork, shallowEqual);
  const usageByNetwork = useSelector(selectUsageByNetwork, shallowEqual);
  const cumulativeUsageByNetwork = useSelector(selectCumulativeUsageByNetwork, shallowEqual);
  const totalUsageByLink: TotalUsageByLink | undefined = useSelector(selectTotalUsageByLink, shallowEqual);
  const usageByLink = useSelector(selectUsageByLink, shallowEqual);
  const cumulativeUsageByLink = useSelector(selectCumulativeUsageByLink, shallowEqual);
  const totalPacketLossByLink = useSelector(selectTotalPacketLossByLink, shallowEqual);
  const serviceAvailability = useSelector(selectServiceAvailability, shallowEqual);

  // Local State
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs());
  const [selectedDatapathUuids, setSelectedDatapathUuids] = useState<string[]>([]);
  const [selectedCharts, setSelectedCharts] = useState<string[]>([
    'Link Usage',
    'Link Usage (cumulative)',
    'Link Usage (comparison)',
    'Service Availability',
    'Network Usage',
    'Network Usage (cumulative)',
    'Network Usage (comparison)',
    'Performance Summary',
  ]);
  const isDataUsageLoading = useSelector(selectDataUsageLoading, shallowEqual);

  const [screenWidth, setScreenWidth] = useState(window.innerWidth - DATA_USAGE_CONTAINER_OFFSET_PX);
  const updateWidth = (ev: any) => {
    if (ev.target.innerWidth > DATA_USAGE_CONTAINER_MIN_WIDTH_PX) {
      setScreenWidth(ev.target.innerWidth - DATA_USAGE_CONTAINER_OFFSET_PX);
    } else {
      setScreenWidth(DATA_USAGE_CONTAINER_MIN_WIDTH_PX);
    }
  };

  // useEffect replaces `componentDidMount` and others
  useEffect(() => {
    window.addEventListener('resize', updateWidth);

    // Removes listener on unmount
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // These refs hold the charts
  const networkUsageTotalChartRef = useRef<HTMLDivElement>();
  const serviceAvailabilityChartRef = useRef<HTMLDivElement>();
  const networkUsageChartRef = useRef<HTMLDivElement>();
  const networkUsageCumulativeChartRef = useRef<HTMLDivElement>();

  const linkUsageTotalChartRef = useRef<HTMLDivElement>();
  const linkUsageChartRef = useRef<HTMLDivElement>();
  const linkUsageCumulativeChartRef = useRef<HTMLDivElement>();

  const performanceSummaryRef = useRef<HTMLDivElement>();

  useEffect(() => {
    // hide the fleet/FTV view map in the case of refresh on any MPT page
    dispatch(setMapHidden(FLEET_MAP_ID)(true));
    dispatch(setMapHidden(DATA_USAGE_PAGE_MAP_ID)(true));
    // get the terminals for the terminal selector
    dispatch(getTerminalsOnce());

    return () => {
      dispatch(setMapHidden(DATA_USAGE_PAGE_MAP_ID)(false));
      dispatch(setMapHidden(FLEET_MAP_ID)(false));
      dispatch(clearDataUsage());
    };
  }, [dispatch]);
  const requestParamsValid = startDate && endDate && selectedDatapathUuids.length && startDate < endDate;

  // Calculate total service availability based on histogram data provided by api endpoint
  const totalServiceAvailabilityPercentage = serviceAvailability
    ? calculateTotalServiceAvailabilityPercentage(serviceAvailability)
    : null;

  const totalPacketLossRounded = !isNil(totalPacketLossByLink?.lossPercentage)
    ? Math.round(totalPacketLossByLink!.lossPercentage * 10) / 10
    : undefined;

  const requestUsageData = () => {
    if (requestParamsValid) {
      const startDateEpochMs = startDate.unix() * SEC_TO_MS_CONVERSION;
      const endDateEpochMs = endDate.unix() * SEC_TO_MS_CONVERSION;
      dispatch(
        requestTotalUsageByNetwork({
          fromMsSinceEpoch: startDateEpochMs,
          toMsSinceEpoch: endDateEpochMs,
          datapathUuids: selectedDatapathUuids,
        }),
      );
      dispatch(
        requestUsageByNetwork({
          fromMsSinceEpoch: startDateEpochMs,
          toMsSinceEpoch: endDateEpochMs,
          datapathUuids: selectedDatapathUuids,
          aggregationInterval: usageByNetwork?.aggregationInterval || 'MONTH',
        }),
      );
      dispatch(
        requestCumulativeUsageByNetwork({
          fromMsSinceEpoch: startDateEpochMs,
          toMsSinceEpoch: endDateEpochMs,
          datapathUuids: selectedDatapathUuids,
          aggregationInterval: cumulativeUsageByNetwork?.aggregationInterval || 'MONTH',
        }),
      );
      dispatch(
        requestTotalUsageByLink({
          fromMsSinceEpoch: startDateEpochMs,
          toMsSinceEpoch: endDateEpochMs,
          datapathUuids: selectedDatapathUuids,
        }),
      );
      dispatch(
        requestUsageByLink({
          fromMsSinceEpoch: startDateEpochMs,
          toMsSinceEpoch: endDateEpochMs,
          datapathUuids: selectedDatapathUuids,
          aggregationInterval: usageByLink?.aggregationInterval || 'MONTH',
        }),
      );
      dispatch(
        requestUsageByLinkCumulative({
          fromMsSinceEpoch: startDateEpochMs,
          toMsSinceEpoch: endDateEpochMs,
          datapathUuids: selectedDatapathUuids,
          aggregationInterval: cumulativeUsageByLink?.aggregationInterval || 'MONTH',
        }),
      );
      dispatch(
        requestTotalPacketLossByLink({
          fromMsSinceEpoch: startDateEpochMs,
          toMsSinceEpoch: endDateEpochMs,
          datapathUuids: selectedDatapathUuids,
        }),
      );
      dispatch(
        requestServiceAvailability({
          fromMsSinceEpoch: startDateEpochMs,
          toMsSinceEpoch: endDateEpochMs,
          datapathUuids: selectedDatapathUuids,
          aggregationInterval: serviceAvailability?.aggregationInterval || 'MONTH',
        }),
      );
    }
  };
  return (
    <Box
      sx={{
        backgroundColor: appTheme.mptPrimaryBackgroundColor,
        minHeight: 'calc(100vh - 52px)',
        minWidth: `${DATA_USAGE_CONTAINER_MIN_WIDTH_PX}px`,
        width: `${screenWidth}px`,
      }}
    >
      <Box minWidth={`${DATA_USAGE_CONTAINER_MIN_WIDTH_PX}px`} width={screenWidth}>
        <DataUsageHeader />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '16px',
            boxSizing: 'border-box',
            borderBottom: `1px solid ${appTheme.secondaryBackgroundColor}`,
          }}
        >
          <DataUsageTerminalSelector
            onSelectboxValueChanged={(values: TerminalListItem[]) => {
              setSelectedDatapathUuids(values.map(terminal => terminal.datapathUuid));
            }}
          />
          <DataUsageChartsSelector
            onSelectboxValueChanged={(values: string[]) => {
              setSelectedCharts(values);
            }}
          />
          <DataUsageDatePicker onStartDateChanged={setStartDate} onEndDateChanged={setEndDate} />
          <Box sx={{ flexGrow: 1 }}>
            <LoadingButton
              sx={{
                marginLeft: '12px',
                color:
                  isDataUsageLoading || !requestParamsValid
                    ? `${appTheme.disabledTextColor} !important`
                    : appTheme.primaryTextColor,
              }}
              color="secondary"
              onClick={requestUsageData}
              disabled={!requestParamsValid}
              loading={isDataUsageLoading}
              loadingIndicator="Loading…"
              endIcon={<RefreshRounded />}
              variant="outlined"
            >
              Generate Reports
            </LoadingButton>
          </Box>
          <Box>
            <DownloadAllChartsDialog
              dataLoaded={
                !!(
                  totalUsageByNetwork &&
                  usageByNetwork &&
                  cumulativeUsageByNetwork &&
                  totalUsageByLink &&
                  usageByLink &&
                  cumulativeUsageByLink &&
                  totalPacketLossByLink &&
                  serviceAvailability
                )
              }
              requestParamsValid={requestParamsValid}
              fromMsSinceEpoch={totalUsageByNetwork ? new Date(totalUsageByNetwork.fromMsSinceEpoch).getTime() : 0}
              toMsSinceEpoch={totalUsageByNetwork ? new Date(totalUsageByNetwork.toMsSinceEpoch).getTime() : 0}
              refs={[
                {
                  name: `Service Availability ${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.fromMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }-${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.toMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }`,
                  displayName: 'Service Availability',
                  chartExportType: 'Service Availability',
                  ref: serviceAvailabilityChartRef,
                },
                {
                  name: `Network Usage (comparison) ${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.fromMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }-${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.toMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }`,
                  displayName: 'Network Usage (comparison)',
                  chartExportType: 'Network Usage (comparison)',
                  ref: networkUsageTotalChartRef,
                },
                {
                  name: `Network Usage ${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.fromMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }-${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.toMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }`,
                  displayName: 'Network Usage',
                  chartExportType: 'Network Usage',
                  ref: networkUsageChartRef,
                },
                {
                  name: `Network Usage (cumulative) ${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.fromMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }-${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.toMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }`,
                  displayName: 'Network Usage (cumulative)',
                  chartExportType: 'Network Usage (cumulative)',
                  ref: networkUsageCumulativeChartRef,
                },
                {
                  name: `Link Usage (comparison) ${
                    totalUsageByLink ? new Date(totalUsageByLink.fromMsSinceEpoch)?.toISOString()?.substring(0, 10) : ''
                  }-${
                    totalUsageByLink ? new Date(totalUsageByLink.toMsSinceEpoch)?.toISOString()?.substring(0, 10) : ''
                  }`,
                  displayName: 'Link Usage (comparison)',
                  chartExportType: 'Link Usage (comparison)',
                  ref: linkUsageTotalChartRef,
                },
                {
                  name: `Link Usage ${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.fromMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }-${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.toMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }`,
                  displayName: 'Link Usage',
                  chartExportType: 'Link Usage',
                  ref: linkUsageChartRef,
                },
                {
                  name: `Link Usage (Cumulative) ${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.fromMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }-${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.toMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }`,
                  displayName: 'Link Usage (cumulative)',
                  chartExportType: 'Link Usage (cumulative)',
                  ref: linkUsageCumulativeChartRef,
                },
                {
                  name: `Performance Summary ${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.fromMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }-${
                    totalUsageByNetwork
                      ? new Date(totalUsageByNetwork.toMsSinceEpoch)?.toISOString()?.substring(0, 10)
                      : ''
                  }`,
                  displayName: 'Performance Summary',
                  chartExportType: 'Performance Summary',
                  ref: performanceSummaryRef,
                },
              ]}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{ paddingX: '16px', boxSizing: 'border-box' }}
        minWidth={`${DATA_USAGE_CONTAINER_MIN_WIDTH_PX - DATA_USAGE_CONTAINER_OFFSET_PX}px`}
        width={DATA_USAGE_CONTAINER_MIN_WIDTH_PX - DATA_USAGE_CONTAINER_OFFSET_PX}
      >
        <Stack direction="row" spacing={'16px'}>
          {selectedCharts.includes('Performance Summary') && (
            <Box
              ref={performanceSummaryRef}
              sx={{
                width: `${(screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX) / 3}px`,
                height: `${DATA_USAGE_CHART_HEIGHT}px`,
              }}
            >
              <PerformanceSummaryChart
                serviceAvailability={totalServiceAvailabilityPercentage}
                packetLoss={totalPacketLossRounded}
                width={(screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX) / 3 - 16}
              />
            </Box>
          )}
          {selectedCharts.includes('Network Usage (comparison)') && (
            <Box
              ref={networkUsageTotalChartRef}
              sx={{
                width: `${(screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX) / 3}px`,
                height: `${DATA_USAGE_CHART_HEIGHT}px`,
              }}
            >
              <TotalNetworkUsageChart
                arclightTotalUsage={totalUsageByNetwork?.kuUsageBytes}
                viasatTotalUsage={totalUsageByNetwork?.kaUsageBytes}
                width={(screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX) / 3 - 16}
              />
            </Box>
          )}
          {selectedCharts.includes('Link Usage (comparison)') && (
            <Box
              ref={linkUsageTotalChartRef}
              sx={{
                width: `${(screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX) / 3}px`,
                height: `${DATA_USAGE_CHART_HEIGHT}px`,
              }}
            >
              <TotalLinkUsageChart
                flTotalUsage={totalUsageByLink?.flUsageBytes}
                rlTotalUsage={totalUsageByLink?.rlUsageBytes}
                width={(screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX) / 3 - 32}
              />
            </Box>
          )}
        </Stack>
        <Box sx={{ marginTop: '16px' }}>
          <Stack direction="column" spacing={'16px'}>
            {selectedCharts.includes('Service Availability') && (
              <Box
                ref={serviceAvailabilityChartRef}
                sx={{
                  height: `${DATA_USAGE_CHART_HEIGHT}px`,
                  minWidth: `${screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX}px`,
                  width: '100%',
                }}
              >
                <ServiceAvailabilityChart width={screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX} />
              </Box>
            )}
            {selectedCharts.includes('Network Usage') && (
              <Box
                ref={networkUsageChartRef}
                sx={{
                  height: `${DATA_USAGE_CHART_HEIGHT}px`,
                  minWidth: `${screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX}px`,
                  width: '100%',
                }}
              >
                <NetworkUsageChart width={screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX} />
              </Box>
            )}
            {selectedCharts.includes('Network Usage (cumulative)') && (
              <Box
                ref={networkUsageCumulativeChartRef}
                sx={{
                  height: `${DATA_USAGE_CHART_HEIGHT}px`,
                  minWidth: `${screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX}px`,
                  width: '100%',
                }}
              >
                <CumulativeNetworkUsageChart width={screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX} />
              </Box>
            )}
            {selectedCharts.includes('Link Usage') && (
              <Box
                ref={linkUsageChartRef}
                sx={{
                  height: `${DATA_USAGE_CHART_HEIGHT}px`,
                  minWidth: `${screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX}px`,
                  width: '100%',
                }}
              >
                <LinkUsageChart width={screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX} />
              </Box>
            )}
            {selectedCharts.includes('Link Usage (cumulative)') && (
              <Box
                ref={linkUsageCumulativeChartRef}
                sx={{
                  height: `${DATA_USAGE_CHART_HEIGHT}px`,
                  minWidth: `${screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX}px`,
                  width: '100%',
                }}
              >
                <CumulativeLinkUsageChart width={screenWidth - DATA_USAGE_CONTAINER_OFFSET_PX} />
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
      {isDataUsageLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '157px',
            minWidth: screenWidth,
            width: `100%`,
            height: `${selectedCharts.length * (DATA_USAGE_CHART_HEIGHT - 100)}px`,
            backgroundColor: '#01010144',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <CircularProgress
            className="progress"
            sx={{
              mt: `${Math.min(window.innerHeight, selectedCharts.length * (DATA_USAGE_CHART_HEIGHT - 100)) * 0.5}px`,
            }}
          />
        </Box>
      )}
      <DataUsageErrorFeedback onRetry={requestUsageData} />
    </Box>
  );
};

export default DataUsageContainer;
